import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  AlertCircle,
  CheckCircle,
  Download,
  FileSpreadsheet,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { downloadProductBulkTemplate, parseProductBulkExcel } from '../../../utils/productBulkExcel';

function validateProduct(product) {
  const errors = [];
  const title = product.title.trim();
  const description = product.description.trim();
  const priceText = String(product.price ?? '').trim();

  if (!title) errors.push('Titulo requerido');
  if (!description) errors.push('Descripcion requerida');

  if (priceText) {
    const price = Number(priceText);
    if (Number.isNaN(price) || price < 0) errors.push('Precio invalido');
  }

  product.originalCategoryNames.forEach((categoryName, index) => {
    if (categoryName && !product.categoryIds[index]) {
      errors.push(`Categoria no reconocida: ${categoryName}`);
    }
  });

  return errors;
}

function buildPayloadProduct(product) {
  const priceText = String(product.price ?? '').trim();
  const categoryIds = product.categoryIds
    .filter((categoryId) => categoryId !== '' && categoryId !== null && categoryId !== undefined)
    .map((categoryId) => Number(categoryId));

  return {
    row_number: product.rowNumber,
    title: product.title.trim(),
    description: product.description.trim(),
    price: priceText ? Number(priceText) : 0,
    category_ids: Array.from(new Set(categoryIds)),
  };
}

function BulkProductUploadModal({ isOpen, onClose, userId, categories, onProductsCreated }) {
  const [products, setProducts] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [search, setSearch] = useState('');
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const baseUrl = process.env.REACT_APP_API_URL || '';

  const productsWithErrors = useMemo(() => (
    products.map((product) => ({
      ...product,
      errors: validateProduct(product),
    }))
  ), [products]);

  const invalidCount = productsWithErrors.filter((product) => product.errors.length > 0).length;
  const validCount = productsWithErrors.length - invalidCount;

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return productsWithErrors;

    return productsWithErrors.filter((product) => {
      const categoryText = product.categoryIds
        .map((categoryId) => categories.find((category) => category.id === Number(categoryId))?.name || '')
        .join(' ');
      return [
        product.title,
        product.description,
        product.price,
        categoryText,
        product.errors.join(' '),
      ].join(' ').toLowerCase().includes(query);
    });
  }, [categories, productsWithErrors, search]);

  if (!isOpen) return null;

  function resetAndClose() {
    setProducts([]);
    setSelectedFile(null);
    setSearch('');
    setParseError('');
    onClose();
  }

  async function handleFileChange(file) {
    if (!file) return;
    setSelectedFile(file);
    setParseError('');

    try {
      const parsedProducts = await parseProductBulkExcel(file, categories);
      if (!parsedProducts.length) {
        setProducts([]);
        setParseError('No se encontraron productos en el archivo.');
        return;
      }
      setProducts(parsedProducts);
    } catch (error) {
      setProducts([]);
      setParseError(error.message || 'No se pudo leer el archivo.');
    }
  }

  function updateProduct(id, field, value) {
    setProducts((current) => current.map((product) => (
      product.id === id ? { ...product, [field]: value } : product
    )));
  }

  function updateProductCategory(id, index, value) {
    setProducts((current) => current.map((product) => {
      if (product.id !== id) return product;
      const categoryIds = [...product.categoryIds];
      const originalCategoryNames = [...product.originalCategoryNames];
      categoryIds[index] = value;
      originalCategoryNames[index] = '';
      return { ...product, categoryIds, originalCategoryNames };
    }));
  }

  function removeProduct(id) {
    setProducts((current) => current.filter((product) => product.id !== id));
  }

  async function handleSubmit() {
    if (!products.length || invalidCount > 0) return;

    setSubmitting(true);
    try {
      const payload = {
        user_id: userId,
        products: products.map(buildPayloadProduct),
      };

      const { data } = await axios.post(`${baseUrl}/api/products/bulk-create`, payload);
      const failedCount = data.failed_count || 0;
      const createdCount = data.created_count || 0;

      if (failedCount > 0) {
        alert(`${createdCount} productos creados. ${failedCount} productos no se pudieron crear.`);
      } else {
        alert(`${createdCount} productos creados correctamente.`);
      }

      onProductsCreated?.();
      resetAndClose();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.error || 'Error al crear productos');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5">
      <div className="w-full max-w-7xl max-h-[92vh] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-900 font-montserrat flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#B1336E]" />
              Carga masiva de productos
            </h2>
            <p className="text-sm text-slate-600 font-montserrat">
              Titulo y descripcion son obligatorios. Precio y categorias son opcionales.
            </p>
          </div>
          <button
            type="button"
            onClick={resetAndClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-3 lg:items-center">
            <button
              type="button"
              onClick={() => downloadProductBulkTemplate(categories)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold font-montserrat hover:bg-slate-800"
            >
              <Download className="w-4 h-4" />
              Descargar plantilla
            </button>

            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                className="hidden"
                onChange={(event) => handleFileChange(event.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-800 text-sm font-semibold font-montserrat hover:border-[#B1336E] hover:text-[#B1336E]"
              >
                <Upload className="w-4 h-4" />
                {selectedFile ? selectedFile.name : 'Subir Excel'}
              </button>
            </div>

            <div className="flex items-center gap-2 text-sm font-semibold font-montserrat">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">
                <CheckCircle className="w-4 h-4" />
                {validCount} validos
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-50 text-red-700">
                <AlertCircle className="w-4 h-4" />
                {invalidCount} errores
              </span>
            </div>
          </div>

          {parseError && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 font-montserrat">
              {parseError}
            </div>
          )}
        </div>

        <div className="px-4 sm:px-6 py-3 border-b border-slate-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por titulo, descripcion, categoria o error..."
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] text-sm font-montserrat"
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {products.length === 0 ? (
            <div className="min-h-72 flex flex-col items-center justify-center text-center p-8">
              <FileSpreadsheet className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-base font-semibold text-slate-800 font-montserrat">Sube una plantilla para revisar productos</p>
              <p className="text-sm text-slate-500 font-montserrat">Podras editar cada fila antes de crear los productos.</p>
            </div>
          ) : (
            <table className="min-w-[1100px] w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-bold text-slate-600 uppercase font-montserrat">Fila</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-slate-600 uppercase font-montserrat">Titulo</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-slate-600 uppercase font-montserrat">Precio</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-slate-600 uppercase font-montserrat">Descripcion</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-slate-600 uppercase font-montserrat">Categorias</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-slate-600 uppercase font-montserrat">Estado</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-slate-600 uppercase font-montserrat">Accion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredProducts.map((product) => (
                  <tr key={product.id} className={product.errors.length ? 'bg-red-50/40' : 'bg-white'}>
                    <td className="px-3 py-3 text-sm text-slate-600 font-montserrat">{product.rowNumber}</td>
                    <td className="px-3 py-3">
                      <input
                        type="text"
                        value={product.title}
                        onChange={(event) => updateProduct(product.id, 'title', event.target.value)}
                        className="w-56 px-2.5 py-2 border border-slate-300 rounded-lg text-sm font-montserrat focus:outline-none focus:border-[#B1336E]"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={product.price}
                        onChange={(event) => updateProduct(product.id, 'price', event.target.value)}
                        placeholder="0"
                        className="w-28 px-2.5 py-2 border border-slate-300 rounded-lg text-sm font-montserrat focus:outline-none focus:border-[#B1336E]"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <textarea
                        value={product.description}
                        onChange={(event) => updateProduct(product.id, 'description', event.target.value)}
                        rows={2}
                        className="w-80 px-2.5 py-2 border border-slate-300 rounded-lg text-sm font-montserrat resize-none focus:outline-none focus:border-[#B1336E]"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-2">
                        {[0, 1, 2].map((index) => (
                          <select
                            key={index}
                            value={product.categoryIds[index] || ''}
                            onChange={(event) => updateProductCategory(product.id, index, event.target.value)}
                            className="w-40 px-2.5 py-2 border border-slate-300 rounded-lg text-sm font-montserrat bg-white focus:outline-none focus:border-[#B1336E]"
                          >
                            <option value="">Sin categoria</option>
                            {categories.map((category) => (
                              <option key={category.id} value={category.id}>{category.name}</option>
                            ))}
                          </select>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      {product.errors.length ? (
                        <div className="space-y-1">
                          {product.errors.map((error) => (
                            <p key={error} className="text-xs font-semibold text-red-700 font-montserrat">{error}</p>
                          ))}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 font-montserrat">
                          <CheckCircle className="w-4 h-4" />
                          Listo
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => removeProduct(product.id)}
                        className="inline-flex items-center justify-center p-2 rounded-lg text-red-600 hover:bg-red-50"
                        aria-label="Eliminar fila"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-4 border-t border-slate-200 bg-white">
          <p className="text-sm text-slate-600 font-montserrat">
            {products.length} productos cargados. Corrige los errores antes de crear.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={resetAndClose}
              className="px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold font-montserrat hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || products.length === 0 || invalidCount > 0}
              className="px-4 py-2.5 rounded-lg bg-[#B1336E] text-white text-sm font-semibold font-montserrat hover:bg-[#B1336E]/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creando...' : `Crear ${validCount} productos`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkProductUploadModal;
